// ────────────────────────────────────────────────────────────────────────────
//  Dashboard.tsx — Redesign mit Live-Daten
//
//  Layout:
//    1. Begrüßungs-Hero mit Live-Uhr + Avatar
//    2. Live-Stempel-Card (nur wenn eingestempelt)
//    3. Bug-Tracker-Banner (Beta-Phase)
//    4. Chat-Preview + Abo-Warnings (zwei Spalten auf Desktop)
//    5. Quick-Stats (Heute / Diese Woche / Ungelesen / Status)
//    6. Bereiche-Grid mit Icon-Tiles
//
//  Komplett theme-aware — alle Farben aus CSS-Vars. Icons monochrome
//  via <Icon> / <GlassIcon>.
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../lib/useAuth'
import { useExtensions } from '../lib/useExtensions'
import { usePunchStatus, usePunchRecords, recordSeconds } from '../lib/usePunch'
import { useConversations, type Conversation } from '../lib/useChat'
import { useT, useLangCode } from '../i18n'
import { Icon, GlassIcon, type IconName } from '../components/Icon'
import { API_ORIGIN } from '../lib/api'

export function Dashboard() {
  const { user } = useAuth()
  const t = useT()
  if (!user) return null

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10 space-y-4 md:space-y-6">
      <HeroGreeting />
      <LivePunchCard />
      <BugBanner />

      <div className="grid md:grid-cols-2 gap-3 md:gap-5">
        <ChatPreviewCard />
        <SubscriptionsCard />
      </div>

      <section>
        <h2 className="mb-2 md:mb-3 text-[11px] font-bold tracking-wider text-app-text-mute uppercase">
          {t('dashboard.glance')}
        </h2>
        <QuickStatsRow />
      </section>

      <section>
        <h2 className="mb-2 md:mb-3 text-[11px] font-bold tracking-wider text-app-text-mute uppercase">
          {t('dashboard.areas')}
        </h2>
        <AreasGrid />
      </section>

      <p className="pt-4 mb-2 text-xs text-app-text-mute text-center">
        {t('dashboard.fullFunction')}
        <br />{t('dashboard.rolloutNote')}
      </p>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Hero — Begrüßung + Live-Uhr + Avatar
// ════════════════════════════════════════════════════════════════════════════
function HeroGreeting() {
  const { user } = useAuth()
  const t = useT()
  const lang = useLangCode()
  const greeting = useGreeting(t)
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  if (!user) return null

  const initials = (user.displayName || user.email || '?')
    .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
  const fName = (user.displayName ?? '').split(/\s+/)[0]

  return (
    <header className="glass-strong p-4 sm:p-5 md:p-7 relative overflow-hidden">
      <div className="absolute -top-20 -right-12 w-72 h-72 rounded-full opacity-50 pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(138,68,238,0.18), transparent 70%)' }} />
      <div className="relative flex items-center gap-3 sm:gap-4">
        {/* Avatar — 44 px auf Mobile, 56 px ab sm. Nur ein Render, CSS skaliert. */}
        <ResponsiveAvatar url={user.avatarUrl} initials={initials} />

        <div className="flex-1 min-w-0">
          <div className="text-[10px] sm:text-xs font-bold tracking-wider text-app-text-mute uppercase">
            {greeting}{fName ? `, ${fName}` : ''}
          </div>
          {/* Tag auf Mobile abgekürzt: "Mo, 6. Mai" — auf md+ voll: "Montag, 6. Mai" */}
          <h1 className="text-lg sm:text-xl md:text-3xl font-bold mt-0.5 truncate leading-tight">
            <span className="md:hidden">
              {now.toLocaleDateString(lang, { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
            <span className="hidden md:inline">
              {now.toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </h1>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl sm:text-2xl md:text-3xl font-mono font-bold tabular-nums leading-tight">
            {now.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}
          </div>
          <div className="text-[10px] text-app-text-mute font-mono tabular-nums">
            {now.toLocaleTimeString(lang, { second: '2-digit' })}
          </div>
        </div>
      </div>
    </header>
  )
}

/** Avatar mit responsivem Size: 44 px auf Mobile, 56 px ab sm. */
function ResponsiveAvatar({ url, initials }: { url: string | null; initials: string }) {
  if (url) {
    const src = url.startsWith('http') ? url : `${API_ORIGIN}${url}`
    return (
      <img
        src={src}
        alt=""
        className="rounded-2xl object-cover shrink-0 w-11 h-11 sm:w-14 sm:h-14"
        style={{ border: '1px solid var(--border-strong)' }}
      />
    )
  }
  return (
    <div
      className="rounded-2xl grid place-items-center font-bold text-white bg-gradient-to-br from-accent to-accent2 shrink-0 w-11 h-11 sm:w-14 sm:h-14 text-base sm:text-xl"
      style={{ border: '1px solid var(--border-strong)' }}
    >
      {initials || '?'}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Live-Punch-Card — nur sichtbar wenn aktuell eingestempelt
// ════════════════════════════════════════════════════════════════════════════
function LivePunchCard() {
  const t = useT()
  const lang = useLangCode()
  const { has, hasBusinessTier, loading } = useExtensions()
  const { status, loading: sLoading } = usePunchStatus({ pollMs: 60_000 })
  const showWidget = has('PunchClock') || hasBusinessTier()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!status?.isClockedIn) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [status?.isClockedIn])

  if (loading || sLoading) return null
  if (!showWidget) return null
  if (!status?.isClockedIn || !status.currentRecord) return null

  const start = new Date(status.currentRecord.clockIn).getTime()
  let totalSec = Math.floor((now - start) / 1000) - (status.currentRecord.pauseSeconds ?? 0)
  if (status.isPaused && status.currentPause) {
    totalSec -= Math.floor((now - new Date(status.currentPause.pausedAt).getTime()) / 1000)
  }
  totalSec = Math.max(0, totalSec)
  const fmt = (s: number) => {
    const h = Math.floor(s / 3600).toString().padStart(2, '0')
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0')
    const sec = (s % 60).toString().padStart(2, '0')
    return `${h}:${m}:${sec}`
  }
  const tint = status.isPaused ? 'amber' : 'teal'

  return (
    <Link to="/app/punch" className="block glass-strong p-4 md:p-5 mb-5 hover:scale-[1.005] transition relative overflow-hidden">
      <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full opacity-50 pointer-events-none"
           style={{ background: `radial-gradient(circle, ${tint === 'amber' ? 'rgba(255,179,0,0.20)' : 'rgba(51,214,112,0.20)'}, transparent 70%)` }} />
      <div className="relative flex items-center gap-4">
        <GlassIcon name={status.isPaused ? 'pause' : 'play'} size={56} tint={tint} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold tracking-wider uppercase"
               style={{ color: status.isPaused ? '#ffb300' : '#33d670' }}>
            {status.isPaused ? t('punch.statusPaused') : t('punch.statusIn')}
          </div>
          <div className="text-2xl md:text-3xl font-mono font-bold tabular-nums tracking-tight mt-0.5">
            {fmt(totalSec)}
          </div>
          <div className="text-[11px] text-app-text-mute mt-0.5">
            {t('punch.sinceLabel', { time: new Date(status.currentRecord.clockIn).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }) })}
          </div>
        </div>
        <Icon name="arrowRight" className="text-app-text-mute shrink-0" />
      </div>
    </Link>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Bug-Tracker-Banner (Beta-Phase)
// ════════════════════════════════════════════════════════════════════════════
function BugBanner() {
  const t = useT()
  return (
    <Link to="/app/bug" className="block glass p-4 md:p-5 mb-5 hover:scale-[1.005] transition group">
      <div className="flex items-center gap-4">
        <GlassIcon name="bug" size={48} tint="coral" />
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider mb-0.5"
               style={{ color: '#ff5b5c' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" />
            {t('dashboard.bugBannerKicker')}
          </div>
          <div className="font-bold text-base md:text-lg leading-tight">{t('dashboard.bugBannerTitle')}</div>
          <div className="text-xs md:text-sm text-app-text-dim mt-0.5"
               dangerouslySetInnerHTML={{ __html: t('dashboard.bugBannerText') }} />
        </div>
        <Icon name="arrowRight" className="text-app-text-mute group-hover:text-accent group-hover:translate-x-0.5 transition shrink-0" />
      </div>
    </Link>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// Chat-Preview-Card — letzte 3 Konversationen mit Live-Update
// ════════════════════════════════════════════════════════════════════════════
function ChatPreviewCard() {
  const t = useT()
  const lang = useLangCode()
  const { user } = useAuth()
  const { items, loading } = useConversations({ pollMs: 15_000 })
  const top = useMemo(() => items.slice(0, 3), [items])
  const totalUnread = items.reduce((s, c) => s + (c.unreadCount || 0), 0)

  return (
    <section className="glass p-5 md:p-6 relative overflow-hidden flex flex-col">
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <GlassIcon name="chat" size={36} tint="brandBlue" />
          <div>
            <h3 className="font-semibold text-sm">{t('chat.title')}</h3>
            {totalUnread > 0 && (
              <div className="text-[10px] text-app-text-mute mt-0.5">
                <span className="font-mono font-bold text-coral">{totalUnread}</span>{' '}
                {t(totalUnread === 1 ? 'dashboard.chatUnreadOne' : 'dashboard.chatUnreadOther', { n: '' }).trim()}
              </div>
            )}
          </div>
        </div>
        <Link to="/app/chat" className="text-xs font-medium text-accent hover:underline">
          {t('common.open')} <Icon name="pinChevron" size={12} className="inline -mt-0.5" />
        </Link>
      </header>

      {loading && items.length === 0 ? (
        <div className="space-y-2 flex-1">
          {[0,1,2].map(i => (
            <div key={i} className="h-12 rounded-xl animate-pulse" style={{ background: 'var(--bg-elev)' }} />
          ))}
        </div>
      ) : top.length === 0 ? (
        <div className="text-center py-6 text-sm text-app-text-mute flex-1 flex flex-col items-center justify-center">
          <Icon name="chat" className="text-app-text-mute opacity-30 mb-2" size={32} />
          {t('chat.empty')}
        </div>
      ) : (
        <ul className="space-y-1.5 flex-1">
          {top.map(c => <ChatPreviewRow key={c.id} c={c} t={t} lang={lang} myId={user?.id ?? ''} />)}
        </ul>
      )}
    </section>
  )
}

function ChatPreviewRow({ c, t, lang, myId }: { c: Conversation; t: any; lang: string; myId: string }) {
  const initials = (c.title || '?').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()
  const last = c.lastMessage
  const time = last ? formatChatTime(last.sentAt, lang, t('dashboard.timeYesterday')) : ''
  const senderPrefix = last && last.senderId === myId ? `${t('chat.sentBy')}: ` : ''
  return (
    <li>
      <Link to="/app/chat" className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-app-elev transition">
        <div className="w-9 h-9 rounded-full grid place-items-center text-[11px] font-bold text-white bg-gradient-to-br from-brandBlue to-accent shrink-0">
          {initials || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium truncate">{c.title}</div>
            {time && <div className="text-[10px] text-app-text-mute shrink-0">{time}</div>}
          </div>
          <div className="text-xs text-app-text-mute truncate">
            {last ? `${senderPrefix}${last.body || '📎'}` : <em className="opacity-60">{t('chat.nothingYet')}</em>}
          </div>
        </div>
        {c.unreadCount > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-accent text-white shrink-0 min-w-[18px] text-center">
            {c.unreadCount}
          </span>
        )}
      </Link>
    </li>
  )
}

function formatChatTime(iso: string, lang: string, yesterdayLabel: string): string {
  const d = new Date(iso)
  const now = new Date()
  const today = now.toDateString() === d.toDateString()
  if (today) return d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
  const y = new Date(now); y.setDate(y.getDate() - 1)
  if (y.toDateString() === d.toDateString()) return yesterdayLabel
  return d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit' })
}

// ════════════════════════════════════════════════════════════════════════════
// Subscriptions-Card — laufende Abos + Warning bei bald-ablaufenden
// ════════════════════════════════════════════════════════════════════════════
function SubscriptionsCard() {
  const t = useT()
  const { items, loading } = useExtensions()

  // Welche laufen in den nächsten 30 Tagen ab?
  const now = Date.now()
  const expiring = items.filter(e => {
    if (!e.expiresAt) return false
    const ms = new Date(e.expiresAt).getTime() - now
    return ms > 0 && ms < 30 * 24 * 3600 * 1000
  }).sort((a, b) => new Date(a.expiresAt!).getTime() - new Date(b.expiresAt!).getTime())

  // Aktive Subs (alles)
  const activeCount = items.filter(e => e.isActive !== false).length

  return (
    <section className="glass p-5 md:p-6 flex flex-col">
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <GlassIcon name="sparkle" size={36} tint="accent" />
          <div>
            <h3 className="font-semibold text-sm">{t('profile.subsTitle')}</h3>
            <div className="text-[10px] text-app-text-mute mt-0.5">
              <span className="font-mono font-bold text-accent">{activeCount}</span>{' '}
              {t('dashboard.activeCountSuffix')}
            </div>
          </div>
        </div>
        <Link to="/app/profile" className="text-xs font-medium text-accent hover:underline">
          {t('common.open')} <Icon name="pinChevron" size={12} className="inline -mt-0.5" />
        </Link>
      </header>

      {loading ? (
        <div className="space-y-2 flex-1">
          {[0,1].map(i => (
            <div key={i} className="h-12 rounded-xl animate-pulse" style={{ background: 'var(--bg-elev)' }} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 flex-1 flex flex-col items-center justify-center">
          <Icon name="sparkle" className="text-app-text-mute opacity-30 mb-2" size={32} />
          <div className="text-sm text-app-text-mute">{t('profile.subsNone')}</div>
          <Link to="/app/shop" className="mt-3 text-xs font-medium text-accent hover:underline">
            {t('shop.title')} →
          </Link>
        </div>
      ) : (
        <div className="flex-1 space-y-3">
          {/* Warning falls bald ablaufende */}
          {expiring.length > 0 && (
            <div className="rounded-xl p-3"
                 style={{ background: 'rgba(255,179,0,0.08)', border: '1px solid rgba(255,179,0,0.25)' }}>
              <div className="flex items-center gap-2 text-xs font-bold mb-1.5"
                   style={{ color: '#ffb300' }}>
                <Icon name="warn" size={14} />
                {expiring.length === 1
                  ? t('dashboard.expiringOne')
                  : t('dashboard.expiringOther', { n: expiring.length })}
              </div>
              <ul className="space-y-1">
                {expiring.slice(0, 3).map(e => {
                  const days = Math.ceil((new Date(e.expiresAt!).getTime() - now) / (24 * 3600 * 1000))
                  return (
                    <li key={e.id} className="text-[11px] flex items-center gap-1.5">
                      <span className="font-medium">{productLabel(e.product, t)}</span>
                      <span className="text-app-text-mute">
                        · {days <= 1 ? t('dashboard.expiresTomorrow') : t('dashboard.expiresInDays', { n: days })}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Liste der aktiven Subs */}
          <ul className="space-y-1.5">
            {items.slice(0, 4).map(e => (
              <li key={e.id} className="flex items-center gap-3 p-2 rounded-lg glass-flat">
                <Icon name={productIcon(e.product)} className="text-app-text-dim shrink-0" size={18} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{productLabel(e.product, t)}</div>
                </div>
                {e.platform === 'apple' && <Icon name="apple" className="text-app-text-mute" size={12} />}
                {e.grantedVia === 'OrgMembership' && (
                  <span className="text-[9px] font-bold uppercase tracking-wider text-app-text-mute">Org</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function productLabel(slug: string, t: ReturnType<typeof useT>): string {
  // i18n-Lookup über den schon vorhandenen `shop.productName.*`-Block.
  // Fallback: Roher Slug (z.B. wenn ein neues Produkt hinzukommt das wir
  // hier noch nicht übersetzt haben).
  const key = `shop.productName.${slug}`
  const translated = t(key)
  return translated && translated !== key ? translated : slug
}
function productIcon(slug: string): IconName {
  const m: Record<string, IconName> = {
    PunchClock: 'punch', TerminalMode: 'punch',
    Recruitment: 'recruit',
    BusinessBasic: 'building', BusinessL: 'building',
    BusinessBasicYearly: 'building', BusinessLYearly: 'building',
    MoreSpace: 'archive',
  }
  return m[slug] ?? 'sparkle'
}

// ════════════════════════════════════════════════════════════════════════════
// Quick-Stats Row
// ════════════════════════════════════════════════════════════════════════════
function QuickStatsRow() {
  const t = useT()
  const { items: records } = usePunchRecords('week')
  const { items: convs }   = useConversations({ pollMs: 30_000 })

  const todayWorked = useMemo(() => {
    const now = new Date(); now.setHours(0,0,0,0)
    return records
      .filter(r => new Date(r.clockIn) >= now)
      .reduce((s, r) => s + recordSeconds(r), 0)
  }, [records])
  const weekWorked = useMemo(() => records.reduce((s, r) => s + recordSeconds(r), 0), [records])
  const unread = convs.reduce((s, c) => s + (c.unreadCount || 0), 0)

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 md:gap-4">
      <StatTile name="punch"   label={t('dashboard.todayWorked')} value={fmtH(todayWorked)} unit="h" tint="teal" />
      <StatTile name="chart"   label={t('dashboard.weekTotal')}   value={fmtH(weekWorked)}  unit="h" tint="brandBlue" />
      <StatTile name="bell"    label={t('dashboard.statUnread')}  value={String(unread)}            tint={unread > 0 ? 'coral' : 'neutral'} />
      <StatTile name="check"   label={t('dashboard.statStatus')}  value="OK"                        tint="teal" />
    </div>
  )
}

function StatTile({ name, label, value, unit, tint }: {
  name: IconName; label: string; value: string; unit?: string
  tint: 'teal' | 'brandBlue' | 'coral' | 'neutral'
}) {
  return (
    <div className="glass p-3 sm:p-3.5 md:p-4 flex items-center gap-2 sm:gap-3">
      <GlassIcon name={name} size={36} tint={tint} className="sm:!w-10 sm:!h-10" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1">
          <span className="text-lg sm:text-xl md:text-2xl font-bold tabular-nums">{value}</span>
          {unit && <span className="text-[10px] sm:text-xs text-app-text-mute">{unit}</span>}
        </div>
        <div className="text-[9px] sm:text-[10px] text-app-text-mute font-medium tracking-wide uppercase truncate">{label}</div>
      </div>
    </div>
  )
}

function fmtH(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const h = total / 3600
  if (h < 1) return Math.round(h * 60).toString() + 'm'
  return h.toFixed(1).replace('.', ',')
}

// ════════════════════════════════════════════════════════════════════════════
// Areas-Grid — Quick-Links zu allen Bereichen
// ════════════════════════════════════════════════════════════════════════════
function AreasGrid() {
  const t = useT()
  const tiles: { icon: IconName; tint: any; label: string; to: string; soon?: boolean }[] = [
    { icon: 'chat',      tint: 'brandBlue', label: t('nav.chat'),    to: '/app/chat' },
    { icon: 'punch',     tint: 'teal',      label: t('nav.punch'),   to: '/app/punch' },
    { icon: 'calendar',  tint: 'accent',    label: t('nav.shifts'),  to: '/app/shifts' },
    { icon: 'wallet',    tint: 'amber',     label: t('nav.payroll'), to: '/app/payroll' },
    { icon: 'recruit',   tint: 'coral',     label: t('nav.recruit'), to: '/app/recruitment' },
    // Krankmeldungen (neues Add-on, gleicher Tile-Style wie die anderen)
    { icon: 'doc',       tint: 'amber',     label: 'Krankmeldungen', to: '/app/sick-leaves' },
    { icon: 'building',  tint: 'accent',    label: t('nav.org'),     to: '/app/org' },
    { icon: 'cart',      tint: 'accent',    label: t('nav.shop'),    to: '/app/shop' },
  ]
  // Mobile 3-Cols (statt 2), ab sm 4-Cols, ab lg 7 → bündige Reihen.
  // Mit 7 Tiles sieht's auf 3-Cols (mobil) so aus: 3+3+1, auf 4-Cols 4+3,
  // auf 7-Cols 7. Alles akzeptabel — und mehr Inhalt sichtbar als bei
  // 2-Cols-mobil-Variante.
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
      {tiles.map(t => <AreaTile key={t.label} {...t} />)}
    </div>
  )
}

function AreaTile({ icon, tint, label, to, soon }: { icon: IconName; tint: any; label: string; to: string; soon?: boolean }) {
  const t = useT()
  const inner = (
    <div className="glass p-2.5 sm:p-3 md:p-4 hover:scale-[1.02] transition relative overflow-hidden">
      {soon && (
        <span className="absolute top-1.5 right-1.5 text-[8px] font-bold tracking-wider px-1 py-0.5 rounded-full uppercase"
              style={{ background: 'rgba(138,68,238,0.15)', color: '#8a44ee', border: '1px solid rgba(138,68,238,0.30)' }}>
          {t('dashboard.soonPill')}
        </span>
      )}
      <GlassIcon name={icon} size={36} tint={soon ? 'neutral' : tint} className="mb-1.5 sm:mb-2 sm:!w-12 sm:!h-12" />
      <div className="text-[11px] sm:text-xs md:text-sm font-semibold leading-tight truncate">{label}</div>
    </div>
  )
  if (soon) return <div className="opacity-60 cursor-not-allowed">{inner}</div>
  return <Link to={to}>{inner}</Link>
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function useGreeting(t: ReturnType<typeof useT>): string {
  const h = new Date().getHours()
  if (h < 5)  return t('dashboard.greetingNight')
  if (h < 11) return t('dashboard.greetingMorning')
  if (h < 18) return t('dashboard.greetingDay')
  return t('dashboard.greetingEvening')
}
